import { Clock, Download, RefreshCw } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';

const formatPrice = (price, pair) => {
  if (!price || price === 0) return '0.00';
  const p = Number(price);
  if (pair && (pair.includes('SHIB') || pair.includes('DOGE') || pair.includes('DGB'))) {
    return p < 0.001 ? p.toFixed(8) : p < 1 ? p.toFixed(6) : p.toFixed(2);
  }
  return p < 0.01 ? p.toFixed(6) : p < 1 ? p.toFixed(4) : p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const TradeHistoryTab = ({
  trades,
  lastTradeRefresh,
  tickCounter,
  onRefresh,
  onExportCSV,
  onExportJSON,
}) => {
  const secondsAgo = lastTradeRefresh
    ? Math.round((Date.now() - lastTradeRefresh.getTime()) / 1000)
    : null;

  return (
    <Card className="matrix-card rounded-none" data-testid="trades-card">
      <CardHeader className="border-b border-[#003B00] p-4">
        <CardTitle className="text-sm uppercase tracking-widest flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" strokeWidth={1.5} />
            TRADE HISTORY
            {secondsAgo !== null && (
              <span className="text-[10px] text-gray-500 font-normal lowercase tracking-normal">
                updated {secondsAgo}s ago
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onRefresh}
              className="matrix-btn rounded-none h-7 px-2 text-xs"
              data-testid="refresh-trades-btn"
            >
              <RefreshCw className="h-3 w-3 mr-1" strokeWidth={1.5} />
              Refresh
            </Button>
            <Button
              onClick={onExportCSV}
              className="matrix-btn rounded-none h-7 px-2 text-xs"
              data-testid="export-csv-btn"
            >
              <Download className="h-3 w-3 mr-1" strokeWidth={1.5} />
              CSV
            </Button>
            <Button
              onClick={onExportJSON}
              className="matrix-btn rounded-none h-7 px-2 text-xs"
              data-testid="export-json-btn"
            >
              <Download className="h-3 w-3 mr-1" strokeWidth={1.5} />
              JSON
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[300px]">
          <table className="matrix-table w-full">
            <thead>
              <tr className="border-b border-[#003B00]">
                <th>TIME</th>
                <th>ACTION</th>
                <th>COIN</th>
                <th>AMOUNT</th>
                <th>PRICE</th>
                <th>VALUE (USD)</th>
                <th>RSI</th>
                <th>TRIGGER</th>
                <th>P/L</th>
              </tr>
            </thead>
            <tbody>
              {trades.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-8 text-[#008F11]">
                    NO TRADES EXECUTED YET
                  </td>
                </tr>
              ) : (
                trades.map((trade, idx) => (
                  <tr key={trade.id || idx} data-testid={`trade-row-${idx}`}>
                    <td className="text-xs">
                      {new Date(trade.timestamp).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true,
                      })}
                    </td>
                    <td>
                      <span className={(trade.action || trade.type) === 'BUY' ? 'trade-buy' : 'trade-sell'}>
                        {trade.action || trade.type}
                      </span>
                    </td>
                    <td className="font-mono text-[#00FF41]">
                      {trade.pair ? trade.pair.split('-')[0] : 'BTC'}
                    </td>
                    <td>{(trade.amount || trade.btc_amount || 0).toFixed(6)}</td>
                    <td>${formatPrice(trade.price || 0, trade.pair)}</td>
                    <td>${(trade.usd_value || 0).toFixed(2)}</td>
                    <td>{trade.rsi != null ? Number(trade.rsi).toFixed(1) : '-'}</td>
                    <td className="text-xs font-mono text-cyan-400">{trade.trigger || '-'}</td>
                    <td className={(trade.profit_loss || trade.profit || 0) >= 0 ? 'number-positive' : 'number-negative'}>
                      {(trade.profit_loss !== null && trade.profit_loss !== undefined) || (trade.profit !== null && trade.profit !== undefined) 
                        ? `${(trade.profit_loss || trade.profit || 0) >= 0 ? '+' : ''}$${(trade.profit_loss || trade.profit || 0).toFixed(6)}` 
                        : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
